import React, { useState, useEffect, useRef, useCallback } from 'react';
import PageHeader from '../components/common/PageHeader';
import {
    Alert,
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    IconButton,
    Tooltip,
    Chip,
    FormControlLabel,
    Switch,
    Autocomplete,
    Checkbox,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
} from '@mui/material';
import {
    SortableHeaderCell,
    makeSortPaginationHandlers,
    AdminTablePagination,
    StatusSnackbars,
} from './_shared/adminListShared_tableUtils';
import {
    Add,
    Delete,
    Close,
    CheckBoxOutlineBlank,
    CheckBox,
    Analytics,
    TableChart,
    Refresh,
} from '@mui/icons-material';
import reportSchedulerApi, { ReportScheduler, CreateReportSchedulerData } from '../services/api/reportSchedulerApi';
import { companyApi } from '../services/api/companyApi';
import { Company } from '../types/company';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const ANALYTICS_TABS = [
    { id: 'emission_statistics', name: 'Emission Statistics' },
    { id: 'transport_modes', name: 'Transport Modes' },
    { id: 'customers', name: 'Customers' },
    { id: 'origin_countries', name: 'Origin Countries' },
    { id: 'destination_countries', name: 'Destination Countries' },
    { id: 'flows', name: 'Flows' },
];

const EMISSION_DASHBOARD_TABS = [
    { id: 'customer', name: 'Customer' },
    { id: 'transport_mode', name: 'Transport Mode' },
    { id: 'origin_country', name: 'Origin Country' },
    { id: 'destination_country', name: 'Destination Country' },
    { id: 'origin_city', name: 'Origin City' },
    { id: 'destination_city', name: 'Destination City' },
];

const ReportSchedulerPage: React.FC = () => {
    const { user } = useAuthStore();
    const [schedulers, setSchedulers] = useState<ReportScheduler[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [companyNameMap, setCompanyNameMap] = useState<Record<number, string>>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalSchedulers, setTotalSchedulers] = useState(0);
    const [sortBy, setSortBy] = useState<string>('created_at');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const [formData, setFormData] = useState<CreateReportSchedulerData>({
        name: '',
        frequency: 'monthly',
        report_type: 'analytics',
        type: 'MTD',
        subtype: 'summary',
        licensee_id: 0,
        target_user_types: null,
    });

    const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
    const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>([]);
    const [targetAllUsers, setTargetAllUsers] = useState(true);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadSchedulers(page, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, sortDir]);

    const companySearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadCompanies = useCallback(async (query?: string) => {
        try {
            setLoadingCompanies(true);
            const response = await companyApi.getAll({
                limit: 10,
                fields: 'id,name',
                ...(query ? { query } : {}),
            });
            const fetched = response.data;
            // Merge selected companies so they always remain visible
            const merged = [...selectedCompanies.filter((sc) => !fetched.some((fc: Company) => fc.id === sc.id)), ...fetched];
            setCompanies(merged);
        } catch (err) {
            console.error('Failed to load companies:', err);
        } finally {
            setLoadingCompanies(false);
        }
    }, [selectedCompanies]);

    const toggleUserType = (type: number) => {
        setSelectedUserTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
        if (validationErrors.userTypes) {
            setValidationErrors(prev => {
                const next = { ...prev };
                delete next.userTypes;
                return next;
            });
        }
    };

    const loadSchedulers = async (newPage = page, pageSize = rowsPerPage) => {
        try {
            setLoading(true);
            const res = await reportSchedulerApi.getAll({ page: newPage + 1, limit: pageSize, sortBy, sortOrder: sortDir });
            setSchedulers(res.data);
            setTotalSchedulers(res.total);
            // Fetch company names for grid display
            const uniqueIds = Array.from(new Set(res.data.map((s: ReportScheduler) => s.licensee_id)));
            if (uniqueIds.length > 0) {
                const compRes = await companyApi.getAll({ limit: uniqueIds.length, fields: 'id,name' });
                const map: Record<number, string> = {};
                compRes.data.forEach((c: any) => { map[c.id] = c.name; });
                setCompanyNameMap(prev => ({ ...prev, ...map }));
            }
        } catch (err: any) {
            console.error('Failed to load schedulers:', err);
            setError('Failed to load schedulers');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setFormData({
            name: '',
            frequency: 'monthly',
            report_type: 'analytics',
            type: 'MTD',
            subtype: 'summary',
            licensee_id: 0,
            target_user_types: null,
        });
        setSelectedCompanies([]);
        setSelectedUserTypes([]);
        setTargetAllUsers(true);
        setValidationErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleInputChange = (field: keyof CreateReportSchedulerData, value: any) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            // Reset subtype if report_type changes
            if (field === 'report_type') {
                newData.subtype = value === 'analytics' ? 'summary' : 'customer';
            }
            return newData;
        });
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const getAccountTypeLabel = (type: AccountType): string => {
        const labels: Record<number, string> = {
            [AccountType.SubCustomerAdmin]: 'Sub-Customer Admin',
            [AccountType.BranchAdmin]: 'Branch Admin',
            [AccountType.SubCompanyAdmin]: 'Sub-Company Admin',
            [AccountType.User]: 'User',
            [AccountType.LicenseeAdmin]: 'Licensee Admin',
            [AccountType.CarbonCareAdmin]: 'CarbonCare Admin',
            [AccountType.MasterAdmin]: 'Master Admin',
        };
        return labels[type] || 'Unknown';
    };

    const handleCreateScheduler = async () => {
        const errors: Record<string, string> = {};
        if (!formData.name.trim()) errors.name = 'Schedule name is required';
        if (!formData.frequency) errors.frequency = 'Frequency is required';
        if (!formData.report_type) errors.report_type = 'Report type is required';
        if (!formData.type) errors.type = 'Filter type is required';
        const validSubtypeIds = (formData.report_type === 'analytics' ? ANALYTICS_TABS : EMISSION_DASHBOARD_TABS).map(t => t.id);
        if (!formData.subtype || !validSubtypeIds.includes(formData.subtype)) {
            errors.subtype = 'Please select a subtype';
        }
        if (selectedCompanies.length === 0) errors.licensees = 'Please select at least one licensee';
        if (!targetAllUsers && selectedUserTypes.length === 0) errors.userTypes = 'Select at least one user type or enable "All user types"';

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors({});

        try {
            setSubmitting(true);
            setError(null);

            // Create one scheduler per selected company
            // Or if the user only selects one (which is the case usually for a periodic report), just one.
            // Based on the prompt "Licensee(select the licensee...)", let's assume one scheduler per creation for simplicity, 
            // but if multiple are selected, we creates multiple.

            for (const company of selectedCompanies) {
                await reportSchedulerApi.create({
                    ...formData,
                    licensee_id: company.id,
                    target_user_types: targetAllUsers ? null : selectedUserTypes,
                });
            }

            setSuccess('Report scheduler(s) created successfully');
            setOpenDialog(false);
            loadSchedulers();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to create scheduler');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id: string) => {
        setDeleteId(id);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteId) return;
        try {
            setSubmitting(true);
            await reportSchedulerApi.delete(deleteId);
            setSuccess('Scheduler deleted');
            setDeleteConfirmOpen(false);
            loadSchedulers();
        } catch (err) {
            console.error('Failed to delete scheduler:', err);
            setError('Failed to delete');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActive = async (scheduler: ReportScheduler) => {
        try {
            await reportSchedulerApi.update(scheduler.id, { active: !scheduler.active });
            loadSchedulers();
        } catch (err) {
            console.error('Failed to update scheduler status:', err);
            setError('Failed to update status');
        }
    };

    const { handleChangePage, handleChangeRowsPerPage, handleSort } = makeSortPaginationHandlers(
        { page, rowsPerPage, sortBy, sortDir },
        setPage,
        setRowsPerPage,
        setSortBy,
        setSortDir,
        (newPage, newSize) => loadSchedulers(newPage, newSize),
    );
    const sortableHeader = (field: string, label: string) => (
        <SortableHeaderCell field={field} label={label} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
    );

    const getReportTypeLabel = (rt: string) => rt === 'analytics' ? 'Analytics Charts' : 'Analytics Data';
    const getSubtypeLabel = (subtype: string) => {
        const tab = [...ANALYTICS_TABS, ...EMISSION_DASHBOARD_TABS].find(t => t.id === subtype);
        return tab ? tab.name : subtype;
    };

    if (user?.accountType !== AccountType.MasterAdmin) {
        return (
            <Box>
                <Alert severity="error">Access Denied: Master Admin Only</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                description="Configure and schedule automated report delivery."
                actions={
                    <>
                        <Button startIcon={<Refresh />} onClick={() => loadSchedulers()} variant="outlined">Refresh</Button>
                        <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenDialog}>
                            New Schedule
                        </Button>
                    </>
                }
            />

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sortDirection={sortBy === 'name' ? sortDir : false}>
                                {sortableHeader('name', 'Report Name')}
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'report_type' ? sortDir : false}>
                                {sortableHeader('report_type', 'Type')}
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'frequency' ? sortDir : false}>
                                {sortableHeader('frequency', 'Frequency')}
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'licensee_id' ? sortDir : false}>
                                {sortableHeader('licensee_id', 'Licensee')}
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'subtype' ? sortDir : false}>
                                {sortableHeader('subtype', 'Subtype (Tab)')}
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'next_run_at' ? sortDir : false}>
                                {sortableHeader('next_run_at', 'Next Run')}
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'active' ? sortDir : false}>
                                {sortableHeader('active', 'Status')}
                            </TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(() => {
                            if (loading) return (
                            Array.from({ length: 8 }, (_, i) => `scheduler-skeleton-${i}`).map((rowKey, idx) => (
                                <TableRow key={rowKey}>
                                    <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Skeleton variant="circular" width={18} height={18} />
                                            <Skeleton variant="text" width={90} />
                                        </Box>
                                    </TableCell>
                                    <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                                    <TableCell><Skeleton variant="text" width={140} /></TableCell>
                                    <TableCell><Skeleton variant="text" width={110} /></TableCell>
                                    <TableCell><Skeleton variant="text" width={150} /></TableCell>
                                    <TableCell><Skeleton variant="rounded" width={34} height={20} sx={{ borderRadius: '10px' }} /></TableCell>
                                    <TableCell align="center"><Skeleton variant="circular" width={28} height={28} sx={{ mx: 'auto' }} /></TableCell>
                                </TableRow>
                            ))
                            );
                            if (schedulers.length === 0) return (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No schedulers found</Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            return (
                                    schedulers.map((scheduler) => (
                                        <TableRow key={scheduler.id} hover>
                                            <TableCell>{scheduler.name}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {scheduler.report_type === 'analytics'
                                                        ? <Analytics fontSize="small" color="primary" />
                                                        : <TableChart fontSize="small" color="success" />}
                                                    <Typography variant="body2">{getReportTypeLabel(scheduler.report_type)}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={scheduler.frequency.toUpperCase()} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                {companyNameMap[scheduler.licensee_id] || `ID: ${scheduler.licensee_id}`}
                                            </TableCell>
                                            <TableCell>{getSubtypeLabel(scheduler.subtype)}</TableCell>
                                            <TableCell>
                                                {scheduler.next_run_at ? new Date(scheduler.next_run_at).toLocaleString() : 'Not set'}
                                            </TableCell>
                                            <TableCell>
                                                <Switch
                                                    checked={scheduler.active}
                                                    onChange={() => toggleActive(scheduler)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Delete">
                                                    <IconButton onClick={() => handleDeleteClick(scheduler.id)}>
                                                        <Delete color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                );
                        })()}
                    </TableBody>
                </Table>
                {!loading && (
                    <AdminTablePagination
                        count={totalSchedulers}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[10]}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Create New Report Schedule
                    <IconButton onClick={handleCloseDialog}><Close /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Schedule Name"
                                placeholder="e.g. Monthly Analytics Summary for Acme Corp"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!validationErrors.frequency}>
                                <InputLabel>Frequency</InputLabel>
                                <Select
                                    label="Frequency"
                                    value={formData.frequency}
                                    onChange={(e) => handleInputChange('frequency', e.target.value)}
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                </Select>
                                {validationErrors.frequency && <FormHelperText>{validationErrors.frequency}</FormHelperText>}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!validationErrors.report_type}>
                                <InputLabel>Report Type</InputLabel>
                                <Select
                                    label="Report Type"
                                    value={formData.report_type}
                                    onChange={(e) => handleInputChange('report_type', e.target.value)}
                                >
                                    <MenuItem value="analytics">Analytics Charts</MenuItem>
                                    <MenuItem value="emission_dashboard">Analytics Data</MenuItem>
                                </Select>
                                {validationErrors.report_type && <FormHelperText>{validationErrors.report_type}</FormHelperText>}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!validationErrors.type}>
                                <InputLabel>Filter Type (Time)</InputLabel>
                                <Select
                                    label="Filter Type (Time)"
                                    value={formData.type}
                                    onChange={(e) => handleInputChange('type', e.target.value)}
                                >
                                    <MenuItem value="MTD">MTD (Month to Date)</MenuItem>
                                    <MenuItem value="L12M">L12M (Last 12 Months)</MenuItem>
                                    <MenuItem value="YTD">YTD (Year to Date)</MenuItem>
                                </Select>
                                {validationErrors.type && <FormHelperText>{validationErrors.type}</FormHelperText>}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!validationErrors.subtype}>
                                <InputLabel>Subtype (Tab)</InputLabel>
                                <Select
                                    label="Subtype (Tab)"
                                    value={formData.subtype}
                                    onChange={(e) => handleInputChange('subtype', e.target.value)}
                                >
                                    {(formData.report_type === 'analytics' ? ANALYTICS_TABS : EMISSION_DASHBOARD_TABS).map(tab => (
                                        <MenuItem key={tab.id} value={tab.id}>{tab.name}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.subtype && <FormHelperText>{validationErrors.subtype}</FormHelperText>}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sx={{ pt: 2, pb: 1 }}>
                            <Autocomplete
                                multiple
                                limitTags={1}
                                options={companies}
                                disableCloseOnSelect
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, val) => option.id === val.id}
                                value={selectedCompanies}
                                onChange={(_, newValue) => {
                                    setSelectedCompanies(newValue);
                                    if (validationErrors.licensees) {
                                        setValidationErrors(prev => {
                                            const next = { ...prev };
                                            delete next.licensees;
                                            return next;
                                        });
                                    }
                                }}
                                onInputChange={(_event, inputValue, reason) => {
                                    if (reason === 'input') {
                                        if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
                                        companySearchTimer.current = setTimeout(() => {
                                            loadCompanies(inputValue || undefined);
                                        }, 300);
                                    }
                                }}
                                onOpen={() => loadCompanies()}
                                filterOptions={(x) => x}
                                loading={loadingCompanies}
                                renderTags={(value, getTagProps) => {
                                    if (value.length === 0) return null;
                                    if (value.length === 1) {
                                        return (
                                            <Chip
                                                label={value[0].name}
                                                size="small"
                                                color="primary"
                                                variant="filled"
                                                sx={{ maxWidth: 200 }}
                                                {...getTagProps({ index: 0 })}
                                            />
                                        );
                                    }
                                    return (
                                        <Chip
                                            label={`${value.length} licensees selected`}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                            onDelete={() => setSelectedCompanies([])}
                                        />
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Licensees"
                                        placeholder={selectedCompanies.length === 0 ? 'Search licensees...' : ''}
                                        error={!!validationErrors.licensees}
                                        helperText={validationErrors.licensees}
                                    />
                                )}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                        {option.name}
                                    </li>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Target User Types</Typography>
                            <FormControlLabel
                                control={<Switch sx={{ml: 1.4, mr: 1, mb: 1}} checked={targetAllUsers} onChange={(e) => setTargetAllUsers(e.target.checked)} />}
                                label="All user types in company"
                            />
                            {!targetAllUsers && (
                                <>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        {Object.values(AccountType).filter(t => typeof t === 'number' && t >= 2).map((type: any) => (
                                            <Chip
                                                key={type}
                                                label={getAccountTypeLabel(type)}
                                                color={selectedUserTypes.includes(type) ? "primary" : "default"}
                                                onClick={() => toggleUserType(type)}
                                            />
                                        ))}
                                    </Box>
                                    {validationErrors.userTypes && (
                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                            {validationErrors.userTypes}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} variant="outlined">Cancel</Button>
                    <Button variant="contained" onClick={handleCreateScheduler} disabled={submitting}>
                        {submitting ? 'Creating...' : 'Create Schedule'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete Scheduler</DialogTitle>
                <DialogContent>Are you sure you want to delete this report schedule?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined">Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={submitting}>Delete</Button>
                </DialogActions>
            </Dialog>

            <StatusSnackbars
                success={success}
                error={error}
                onClearSuccess={() => setSuccess(null)}
                onClearError={() => setError(null)}
            />
        </Box>
    );
};

export default ReportSchedulerPage;
